/**
 * Permission codes — single source of truth, shared by ERP backend seed,
 * backend @RequirePermissions decorators, and frontend PermissionGate.
 *
 * 代码格式: `<module>:<object>:<action>` or `<module>:<action>`.
 *
 * 注意: 这里定义的 code 字符串必须与 backend
 * `system/system-permissions.service.ts` initPermissions() seed 的 code
 * 完全一致, 否则 DB 里没有对应 permission 行, guard 永远拒绝.
 */
export const PERMISSIONS = {
    DASHBOARD: {
        VIEW: 'dashboard:view',
        WIDGET_MANAGE: 'dashboard:widget:manage',
    },
    AI: {
        VIEW: 'ai:view',
        CHAT: 'ai:chat',
        GENERATE: 'ai:generate',
    },
    ANNOUNCEMENT: {
        VIEW: 'announcement:view',
        CREATE: 'announcement:create',
        MANAGE: 'announcement:manage',
    },
    APPROVAL: {
        VIEW: 'approval:view',
        PROCESS: 'approval:process',
        MANAGE: 'approval:manage',
        DELEGATE: 'approval:delegate',
        QUERY: 'approval:query',
        FLOW_MANAGE: 'approval:flow:manage',
    },
    CRM: {
        VIEW: 'crm:view',
        // 粗粒度别名 (保留给前端 PermissionGate, 后端装饰器用细粒度)
        CREATE: 'crm:create',
        EDIT: 'crm:edit',
        DELETE: 'crm:delete',
        // 细粒度 (backend seed 绑定)
        CUSTOMER: { READ: 'crm:customer:read', WRITE: 'crm:customer:write' },
        LEAD: { MANAGE: 'crm:lead:manage' },
        OPPORTUNITY: { MANAGE: 'crm:opportunity:manage' },
        QUOTE: { MANAGE: 'crm:quote:manage' },
        ORDER: { READ: 'crm:order:read', WRITE: 'crm:order:write' },
    },
    FINANCE: {
        VIEW: 'finance:view',
        CREATE: 'finance:create',
        MANAGE: 'finance:manage',
        EXPORT: 'finance:export',
        IMPORT: 'finance:import',
        REPORT_VIEW: 'finance:report:view',
        JOURNAL: {
            READ: 'finance:journal:read',
            WRITE: 'finance:journal:write',
            APPROVE: 'finance:journal:approve',
            POST: 'finance:journal:post',
            REVERSE: 'finance:journal:reverse',
        },
        AR_MANAGE: 'finance:ar:manage',
        AP_MANAGE: 'finance:ap:manage',
        AP: {
            PREPAYMENT: {
                VIEW: 'finance:ap:prepayment:view',
                CREATE: 'finance:ap:prepayment:create',
                APPLY: 'finance:ap:prepayment:apply',
                CANCEL: 'finance:ap:prepayment:cancel',
            },
        },
        FA: {
            AUC: {
                VIEW: 'finance:fa:auc:view',
                CREATE: 'finance:fa:auc:create',
                EXPENSE: 'finance:fa:auc:expense',
                TRANSFER: 'finance:fa:auc:transfer',
                CANCEL: 'finance:fa:auc:cancel',
            },
        },
        BANK_MANAGE: 'finance:bank:manage',
        ASSET_READ: 'finance:asset:read',
        ASSET_MANAGE: 'finance:asset:manage',
        TAX_READ: 'finance:tax:read',
        TAX_MANAGE: 'finance:tax:manage',
        CLOSING_MANAGE: 'finance:closing:manage',
        COA_READ: 'finance:coa:read',
        COA_MANAGE: 'finance:coa:manage',
        BUDGET_READ: 'finance:budget:read',
        BUDGET_MANAGE: 'finance:budget:manage',
        IFRS_READ: 'finance:ifrs:read',
        IFRS_MANAGE: 'finance:ifrs:manage',
        OPENING_BALANCE: {
            READ: 'finance:opening-balance:read',
            IMPORT: 'finance:opening-balance:import',
            RECONCILE: 'finance:opening-balance:reconcile',
        },
    },
    HRM: {
        VIEW: 'hrm:view',
        ORG: { READ: 'hrm:org:read', WRITE: 'hrm:org:write' },
        EMPLOYEE: { READ: 'hrm:employee:read', WRITE: 'hrm:employee:write', DELETE: 'hrm:employee:delete' },
        ATTENDANCE: { READ: 'hrm:attendance:read', WRITE: 'hrm:attendance:write', CLOCK: 'hrm:attendance:clock' },
        LEAVE: { READ: 'hrm:leave:read', APPLY: 'hrm:leave:apply', APPROVE: 'hrm:leave:approve' },
        OVERTIME: { READ: 'hrm:overtime:read', APPLY: 'hrm:overtime:apply', APPROVE: 'hrm:overtime:approve' },
        TRAVEL: { READ: 'hrm:travel:read', APPLY: 'hrm:travel:apply', APPROVE: 'hrm:travel:approve' },
        TRANSFER: { READ: 'hrm:transfer:read', APPLY: 'hrm:transfer:apply', APPROVE: 'hrm:transfer:approve' },
        RESIGNATION: { READ: 'hrm:resignation:read', APPLY: 'hrm:resignation:apply', APPROVE: 'hrm:resignation:approve' },
        CONFIRMATION: { READ: 'hrm:confirmation:read', APPLY: 'hrm:confirmation:apply', APPROVE: 'hrm:confirmation:approve' },
        RECRUITMENT: { READ: 'hrm:recruitment:read', WRITE: 'hrm:recruitment:write' },
        PAYROLL: { READ: 'hrm:payroll:read', WRITE: 'hrm:payroll:write' },
    },
    MESSAGE: {
        VIEW: 'message:view',
        SEND: 'message:send',
        GROUP_MANAGE: 'message:group:manage',
    },
    MAIL: {
        VIEW: 'mail:view',
        SEND: 'mail:send',
        MANAGE: 'mail:manage',
    },
    TASK: {
        VIEW: 'task:view',
        CREATE: 'task:create',
        MANAGE: 'task:manage',
        ASSIGN: 'task:assign',
    },
    PROJECT: {
        VIEW: 'project:view',
        READ: 'project:read',
        CREATE: 'project:create',
        MANAGE: 'project:manage',
        BUDGET_MANAGE: 'project:budget:manage',
        REPORT: 'project:report',
    },
    PRODUCTION: {
        VIEW: 'production:view',
        CREATE: 'production:bom:write',
        MANAGE: 'production:bom:write',
        BOM: { READ: 'production:bom:read', WRITE: 'production:bom:write' },
        ORDER: { READ: 'production:order:read', WRITE: 'production:order:write' },
        SCHEDULE: 'production:schedule',
        REPORT: 'production:report',
    },
    SRM: {
        VIEW: 'srm:view',
        // 粗粒度别名 (前端)
        CREATE: 'srm:create',
        MANAGE: 'srm:manage',
        // 细粒度 (backend seed)
        SUPPLIER: { READ: 'srm:supplier:read', WRITE: 'srm:supplier:write' },
        PR: { READ: 'srm:pr:read', WRITE: 'srm:pr:write' },
        PO: { READ: 'srm:po:read', WRITE: 'srm:po:write', APPROVE: 'srm:po:approve' },
        GR: { MANAGE: 'srm:gr:manage' },
    },
    WMS: {
        VIEW: 'wms:view',
        // 粗粒度别名
        CREATE: 'wms:create',
        MANAGE: 'wms:manage',
        // 细粒度
        WAREHOUSE: { MANAGE: 'wms:warehouse:manage' },
        INVENTORY: { READ: 'wms:inventory:read', ADJUST: 'wms:inventory:adjust' },
        INBOUND: { MANAGE: 'wms:inbound:manage' },
        OUTBOUND: { MANAGE: 'wms:outbound:manage' },
        TRANSFER: { MANAGE: 'wms:transfer:manage' },
    },
    OA: {
        VIEW: 'oa:view',
        CONTRACT: { READ: 'oa:contract:read', WRITE: 'oa:contract:write' },
        SEAL: {
            READ: 'oa:seal:read',
            WRITE: 'oa:seal:write',
            APPLY: 'oa:seal:apply',
            MANAGE: 'oa:seal:manage',
        },
        DOCUMENT: {
            READ: 'oa:document:read',
            WRITE: 'oa:document:write',
            APPLY: 'oa:document:apply',
            APPROVE: 'oa:document:approve',
            REJECT: 'oa:document:reject',
            DISPATCH: 'oa:document:dispatch',
            ARCHIVE: 'oa:document:archive',
            MANAGE: 'oa:document:manage',
        },
        LICENSE: {
            READ: 'oa:license:read',
            WRITE: 'oa:license:write',
            MANAGE: 'oa:license:manage',
        },
        MEETING: {
            READ: 'oa:meeting:read',
            WRITE: 'oa:meeting:write',
            BOOK: 'oa:meeting:book',
            MANAGE: 'oa:meeting:manage',
        },
        VEHICLE: {
            READ: 'oa:vehicle:read',
            WRITE: 'oa:vehicle:write',
            APPLY: 'oa:vehicle:apply',
            MANAGE: 'oa:vehicle:manage',
        },
        VISITOR: {
            READ: 'oa:visitor:read',
            WRITE: 'oa:visitor:write',
            REGISTER: 'oa:visitor:register',
            MANAGE: 'oa:visitor:manage',
        },
        SUPPLY: {
            READ: 'oa:supply:read',
            WRITE: 'oa:supply:write',
            APPLY: 'oa:supply:apply',
            MANAGE: 'oa:supply:manage',
        },
        GATE_PASS: {
            READ: 'oa:gatepass:read',
            WRITE: 'oa:gatepass:write',
            APPLY: 'oa:gatepass:apply',
            APPROVE: 'oa:gatepass:approve',
            MANAGE: 'oa:gatepass:manage',
            GUARD: 'oa:gatepass:guard',
        },
        GUARD: { VIEW: 'oa:guard:view', MANAGE: 'oa:guard:manage', CONFIRM: 'oa:guard:confirm' },
    },
    CONTROLLING: {
        VIEW: 'controlling:view',
        COST: { READ: 'controlling:cost:read', MANAGE: 'controlling:cost:manage' },
        COST_CENTER: {
            VIEW: 'controlling:cost_center:view',
            MANAGE: 'controlling:cost_center:manage',
            DRILL: 'controlling:cost_center:drill',
        },
        PROFIT: { READ: 'controlling:profit:read', MANAGE: 'controlling:profit:manage' },
        PROFIT_CENTER: {
            VIEW: 'controlling:profit_center:view',
            MANAGE: 'controlling:profit_center:manage',
            SYNC: 'controlling:profit_center:sync',
        },
        OVERHEAD: {
            VIEW: 'controlling:overhead:view',
            MANAGE: 'controlling:overhead:manage',
            SETTLE: 'controlling:overhead:settle',
        },
        ANALYSIS: 'controlling:analysis',
    },
    SYSTEM: {
        VIEW: 'system:view',
        MANAGE: 'system:manage',
        USER: { READ: 'system:user:read', MANAGE: 'system:user:manage' },
        ROLE: { READ: 'system:role:read', MANAGE: 'system:role:manage' },
        PERMISSION: { MANAGE: 'system:permission:manage' },
        CONFIG: { MANAGE: 'system:config:manage' },
        AUDIT: { VIEW: 'system:audit:view' },
        LOG: { VIEW: 'system:log:view' },
    },
    BUDGET: {
        VIEW: 'budget:view',
        PLAN: {
            READ: 'budget:plan:read',
            WRITE: 'budget:plan:write',
            APPROVE: 'budget:plan:approve',
        },
        EXECUTE: { READ: 'budget:execute:read' },
        ADJUST: 'budget:adjust',
    },
    /**
     * 集团总览 — WITTY (companyId=1000) 跨工厂只读视图。
     * 只有总部账号 / SUPER_ADMIN / ADMIN 应当持有；PSP/YPC 用户拒绝。
     */
    GROUP: {
        VIEW: 'group:view',
    },
};
/** 管理员级角色 — 特殊身份, 绕过部分 UI 限制 (非 guard 逻辑) */
export const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN'];
/**
 * 财务权限分组 (给 backend @RequirePermissions 用) —
 * 取代原 finance-roles.ts 的 ALL_FINANCE_ROLES/ACCOUNTANT_AND_ABOVE/MANAGER_AND_ABOVE.
 *
 * 层级:
 *   READ_PERMS    — VIEWER/ACCOUNTANT/MGR/ADMIN 都有
 *   WRITE_PERMS   — ACCOUNTANT+
 *   MANAGE_PERMS  — FINANCE_MANAGER+
 */
export const FINANCE_READ_PERMS = [PERMISSIONS.FINANCE.VIEW];
export const FINANCE_WRITE_PERMS = [PERMISSIONS.FINANCE.JOURNAL.WRITE];
export const FINANCE_MANAGE_PERMS = [PERMISSIONS.FINANCE.JOURNAL.APPROVE];
